import { type DataStreamBaseTileConfig } from '@squaredup/data-streams';
import LoadingSpinner from 'components/LoadingSpinner';
import NoDataPlaceholder from 'components/NoDataPlaceholder';
import { useDataStreamConfig } from 'dashboard-engine/hooks/useDataStreamConfig';
import stringify from 'fast-json-stable-stringify';
import { FC, useEffect, useMemo, useReducer, useRef, type Dispatch, type SetStateAction } from 'react';
import { useQueryClient } from 'react-query';
import { StepProgressButton } from '../../StepProgressButton';
import { StepTitleAndControls } from '../../StepTitleAndControls';
import { dataStreamMetadataReducer } from './metadataEditor/MetadataEditorReducer';
import { sampleValueFrom } from './metadataEditor/MetadataEditorState';
import { fromDataStreamMetadata } from './metadataEditor/MetadataEditorStreamData';
import { MetadataEditorTable } from './metadataEditor/MetadataEditorTable';
import { toTileMetadata } from './metadataEditor/MetadataEditorTileState';

export interface DataStreamMetadataProps {
    config: DataStreamBaseTileConfig;
    setConfig: Dispatch<SetStateAction<DataStreamBaseTileConfig>>;
}

/**
 * Compare the values of two objects, ignoring properties that exist only in one object.
 */
export const propertiesInBothAreEqual = (a: object, b: object): boolean => {
    return Object.entries(a)
        .filter(([k]) => k in b)
        .every(([k, v]) => {
            const otherValue = (b as { [x: string]: unknown })[k];

            return (v == null && otherValue == null) || stringify(v) === stringify(otherValue);
        });
};

export const DataStreamMetadata: FC<DataStreamMetadataProps> = ({ config, setConfig }) => {
    const queryClient = useQueryClient();
    const metadataColumnNames = useRef<string>('');

    const { data, isLoaded, isConfigured, queryKey, dataUpdatedAt } = useDataStreamConfig(config, {
        keepPreviousData: true,
        onSuccess: (streamData) => {
            // Initialize metadataColumnNames
            if (!metadataColumnNames.current) {
                stringify(streamData.metadata.columns);
            }
        }
    });

    const [state, dispatch] = useReducer(dataStreamMetadataReducer, undefined, () =>
        fromDataStreamMetadata(data, config.dataStream?.metadata ?? [])
    );

    // useEffect to avoid updating config during rendering and triggering
    // 'Cannot update a component while rendering a different component'
    useEffect(() => {
        const asMetadata = toTileMetadata(state);

        if (state.tileConfigUpdatePending) {
            // Only update the config if something has changed
            if (stringify(asMetadata) !== stringify(config.dataStream?.metadata)) {
                setConfig({
                    ...config,
                    dataStream: {
                        ...config.dataStream,
                        metadata: asMetadata
                    }
                });
            }

            // Cancel the current query as it's now not needed
            queryClient.cancelQueries({ queryKey });
            dispatch({ type: 'saveTileConfig' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.tileConfigUpdatePending, stringify(config.dataStream?.metadata)]);

    // We want to only assign the getSampleValue when data has changed
    // (we rely on the updatedAt to achieve this)
    const getSampleValue = useMemo(
        () => sampleValueFrom(data.rows, data.metadata.columns),
        [dataUpdatedAt] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const columnNames = stringify(data.metadata.columns.map(({ name }) => name));
    const columnsIsBeingCloned = [...state.columnMap.values()].some((column) => column.isClone && column.isBeingCloned);

    // If there is a new column in the data and we know there is a column being cloned reset the reducer
    // state to reflect the new columns
    useEffect(() => {
        // Sometimes we get to the metadata tab before data is loaded and therefore state.columnMap.size
        // is 0. Once it's loaded, we want to reset the state to ensure we're displaying data in the table
        const isNowReturningData = Boolean(columnNames && !state.columnMap.size);

        if (columnNames !== metadataColumnNames.current && (columnsIsBeingCloned || isNowReturningData)) {
            dispatch({
                type: 'reset',
                state: fromDataStreamMetadata(data, config.dataStream?.metadata ?? [])
            });
        }

        metadataColumnNames.current = columnNames;
    }, [columnNames, columnsIsBeingCloned, config.dataStream?.metadata, data, state.columnMap.size]);

    if (!isConfigured) {
        return (
            <div className='h-full p-sm'>
                <NoDataPlaceholder message='Column editing will be available after selecting objects and/or configuring parameters.' />
            </div>
        );
    }

    if (!isLoaded) {
        // Show loading spinner until first data load is complete
        return (
            <div className='flex items-center justify-center flex-1 w-full h-full'>
                <LoadingSpinner className='m-auto' />
            </div>
        );
    }

    return (
        <div className='flex flex-col w-full h-full min-h-0 py-4' id='metadataStep'>
            <div className='pb-4 pl-6 pr-5 mb-4 border-b border-dividerTertiary'>
                <StepTitleAndControls title='Format columns' />
            </div>

            <div className='flex flex-col py-0 pl-6 pr-5 space-y-6 overflow-hidden'>
                <div className='relative w-full h-full min-w-0 min-h-0 overflow-auto text-sm scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary'>
                    <MetadataEditorTable
                        state={state}
                        getSampleValue={getSampleValue}
                        dispatch={dispatch}
                        config={config}
                    />
                </div>
            </div>

            <div className='flex items-center justify-end flex-shrink-0 pr-5 mt-6'>
                <StepProgressButton />
            </div>
        </div>
    );
};
